<template>
  <rmp-base-modal
    v-model="isOpen"
    extra-classes="EmbedFormModal Modal--large"
    prevent-outside-click
  >
    <template #modal-header>
      <h2 class="u-text-center">
        Embed event: {{ event.name }}
      </h2>
    </template>
    <template #modal-body>
      <div class="u-flex ">
        <section
          v-if="event.type !== 'dynamic'"
          class="Section--left"
          style="max-width: 400px;"
        >
          <h3>Options</h3>
          <rmp-checkbox
            v-if="event.type !== 'dynamic'"
            v-model="customThankyouPage"
            label="I want to use my own Thank you page"
            :value="true"
            @change="handleToggleCustomPage"
          />
          <div
            v-if="customThankyouPage"
            class="Notice"
          >
            <h3>Thank you page URL</h3>
            <rmp-input v-model.trim="fields.url" />
            <p class="Notice-copy">
              The user will be redirected to the thank you page after submitting the form.
            </p>
          </div>
        </section>
        <section class="Section--right">
          <div :class="{'Blurred': ui.showGenerateFormButton}">
            <div class="Event-container">
              <div style="flex: 1; margin-right: 20px;">
                <h3 class="Input-header">
                  Event Link
                </h3>
                <p>Use this link or QR code to share the event</p>
                <rmp-input
                  class="EmbedLink-input"
                  :model-data="embedUrl"
                  @click="copyLink($event)"
                />
                <span
                  v-if="ui.linkCopied"
                  class="LinkCopied"
                >Link copied!</span>
              </div>
              <div
                class="Event-qr-container"
                @mouseover="ui.showQrCodeDownloadButtons = true"
                @mouseout="ui.showQrCodeDownloadButtons = false"
              >
                <img
                  :src="embedQrCode.png"
                  class="Event-qr-code"
                >
                <div v-show="ui.showQrCodeDownloadButtons">
                  <button
                    class="Download-qr-code-png-button Button Button--green"
                    @click="downloadQrCode('png')"
                  >
                    Download PNG
                  </button>
                  <button
                    class="Download-qr-code-svg-button Button Button--green"
                    @click="downloadQrCode('svg')"
                  >
                    Download SVG
                  </button>
                </div>
              </div>
            </div>
            <h3 class="Input-header">
              Event Code
            </h3>
            <p>Use this code to embed the event in a web page.</p>
            <rmp-text-area
              :model-data="embeddedScriptGist"
            />
            <a
              target="_blank"
              :href="embedUrl"
              class="Button Button--green"
            >
              View Event
            </a>
          </div>
          <button
            v-if="ui.showGenerateFormButton"
            :class="[{'is-loading': ui.generateFormButtonLoading},'GenerateFormButton Button Button--green']"
            @click="handleGenerateForm"
          >
            Generate Form
          </button>
        </section>
      </div>
    </template>
  </rmp-base-modal>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import RmpBaseModal from '@rmpenterprise/base-modal';
import RmpInput from '@rmpenterprise/form-input';
import RmpTextArea from '@rmpenterprise/form-text-area';
import RmpCheckbox from '@rmpenterprise/form-checkbox';
import ModalActionsMixin from './mixins/modal-actions-mixin';

export default {
  components: {
    RmpBaseModal,
    RmpInput,
    RmpTextArea,
    RmpCheckbox,
  },
  mixins: [ModalActionsMixin],
  props: {
    event: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      fields: {
        url: null,
      },
      currentFieldsUrl: null,
      customThankyouPage: false,
      eventUUID: this.event.eventUUID,
      embedScriptSrc: this.event.embedded_code_script_source,
      embedQrCode: this.event.embedded_code_generation_qr_code,
      embedUrl: this.event.embedded_code_generation_default_url,
      baseEmbedUrl: this.event.embedded_code_generation_default_url,
      params: new URLSearchParams(this.queryString),
      embeddedScriptGist: this.event.embedded_default_code_gist,
      ui: {
        linkCopied: false,
        showGenerateFormButton: false,
        generateFormButtonLoading: false,
        showQrCodeDownloadButtons: false,
      },
    };
  },
  computed: {
    ...mapState('events', {}),
  },
  watch: {
    'fields.url': {
      handler() {
        if (this.fields.url === '' || this.fields.url === null) {
          this.ui.showGenerateFormButton = false;
          return;
        }

        this.ui.showGenerateFormButton = this.currentFieldsUrl !== this.fields.url;
      },
    },
  },
  methods: {

    ...mapActions('events', ['generateEventEmbedCode']),
    handleToggleCustomPage() {
      if (!this.customThankyouPage) {
        // Reset emebed codes and links since generatred codes will be stale if a user decides to remove custom page
        this.embeddedScriptGist = this.event.embedded_default_code_gist;
        this.embedUrl = this.baseEmbedUrl;
        this.embedQrCode = this.embedded_code_generation_qr_code;
        this.fields.url = null;
        this.currentFieldsUrl = null;
        this.queryString = { ...this.queryString };
      }
    },
    handleGenerateForm() {
      this.ui.generateFormButtonLoading = true;

      const eventId = this.event.id;

      const payload = {
        ...this.queryString,
        url: this.fields.url,
      };

      this.generateEventEmbedCode({ eventId, payload })
        .then(({ data }) => {
          console.log('data');
          this.ui.generateFormButtonLoading = false;
          this.embedUrl = data.url;
          this.embeddedScriptGist = data.embeddedScriptGist;
          this.embedQrCode = data.embeddedQrCode;
          this.params = new URLSearchParams(data.url);

          // Store thank you page url for comparison with new values entered by user
          // A new url will require them to generate a new form embed code each time
          this.currentFieldsUrl = this.fields.url;
          this.ui.showGenerateFormButton = false;
        })
        .catch(({ data }) => {
          this.ui.generateFormButtonLoading = false;
          let { message } = data.data;
          if (data.meta[0]) {
            message = data.meta[0].message;
          }
          this.ui.submitLoading = false;
          this.$bus.$emit('displayMsg', {
            text: message,
            type: 'error',
          });
        });
    },
    copyLink(ev) {
      ev.target.select();

      /* Copy the text inside the text field */
      document.execCommand('copy');
      this.ui.linkCopied = true;

      setTimeout(() => {
        this.ui.linkCopied = false;
      }, 2000);
    },
    downloadQrCode(fileFormat) {
      const link = document.createElement('a');
      link.href = (fileFormat === 'svg') ? this.embedQrCode.svg : this.embedQrCode.png;
      link.download = `${this.event.name}-qr-code.${fileFormat}`;
      link.click();
      link.remove();
    },
  },
};
</script>

<style scoped lang="less">

.EmbedLink-input {
  cursor: pointer;
}

.LinkCopied {
  position: relative;
  bottom: 24px;
  color: #39b546;
  font-weight: bold;
  float: right;
}

.Blurred {
  opacity: 0.2;
  pointer-events: none;
}

.GenerateFormButton {
  position: absolute;
  top: 50%;
  right: 18%;
}

.Notice {
  border: 1px solid #CCCCCC;
  border-radius: 3px;
}

.ColorSwatch {
  border: 1px solid #CCCCCC;
  border-radius: 8px;
  display: flex;
  margin-right: 25px;

  .Color {
    height: 40px;
    width: 43px;
    padding: 3px;
  }

  .Color-value {
    padding: 0px 32px 0px 11px;
  }

  .Color-value.Form-field {
    margin: 0;
    padding: 0;
  }
}

.Colors-container {
  display: flex;
  flex-wrap: wrap;

  .input-group {
    flex-basis: 50%;
    margin-bottom: 10px;
  }
}

.ColorSwatch-container {
  position: absolute;
  top: 129px;
  left: 100px;

  .Icon-s {
    position: absolute;
    display: inline-block;
    top: -21px;
    right: 1px;
    padding: 2px 5px;
    cursor: pointer;
    text-align: center;
    border: 1px solid #CCCCCC;
  }
}

.Input-header {
  margin-bottom: 5px !important;
}

.Event-container{
  display: flex;
}

.Event-qr-code{
  justify-content: center;
}

.Event-qr-container{
  width: 180px;
  height: 180px;
  border: 1px solid lightgrey;
  border-radius: 0.25rem;
  .Download-qr-code-png-button {
    position: relative;
    margin-top: -150px;
    margin-left: 25px;
  }

  .Download-qr-code-svg-button {
    position: relative;
    margin-top: -285px;
    margin-left: 25px;
  }

}
</style>
